<template>
  <b-col cols="12">
    <label class="form-group__label"> Access control list </label>

    <!-- 1. add permissions (campaigns are assigned later, in the list below) -->
    <b-form v-if="aclItems.length" class="acl-add my-2">
      <b-row>
        <b-col cols="12" md="10" class="px-1 pb-2">
          <label class="acl-add__label">Add permissions</label>
          <div class="acl-add__perm-list">
            <b-form-checkbox-group v-model="form.actions" :options="aclItems" stacked />
          </div>
        </b-col>

        <b-col cols="12" md="2" class="px-1 pb-2 d-flex align-items-end">
          <b-button variant="primary" block :disabled="!form.actions.length" @click="add">
            Add
          </b-button>
        </b-col>
      </b-row>
    </b-form>

    <!-- 2. the list: tick rows, then assign them to campaigns -->
    <b-table v-if="items.length" striped :fields="fields" :items="items" stacked="sm">
      <template #head(select)>
        <b-form-checkbox
          :checked="allSelected"
          :indeterminate="someSelected"
          @change="toggleSelectAll"
        />
      </template>

      <template #cell(select)="{ item }">
        <b-form-checkbox
          :checked="selectedActions.includes(item.action)"
          @change="(checked) => toggleRow(item.action, checked)"
        />
      </template>

      <template #cell(campaign_ids)="{ item }">
        <template v-if="item.campaign_ids && item.campaign_ids.length">
          <b-badge
            v-for="cid in item.campaign_ids"
            :key="cid"
            variant="light"
            class="acl-cmp-tag mr-1 mb-1"
          >
            {{ cid }}
            <b-icon-x
              class="acl-cmp-tag__x"
              role="button"
              @click="removeCampaign(item.action, cid)"
            />
          </b-badge>
        </template>
        <span v-else>All</span>
      </template>

      <template #cell(actions)="{ index }">
        <b-icon-trash-fill variant="danger" role="button" font-scale="1.2" @click="remove(index)" />
      </template>
    </b-table>

    <!-- 3. assign the selected permissions to campaign(s) -->
    <div v-if="items.length" class="acl-assign my-2">
      <b-row class="align-items-end">
        <b-col cols="12" md="8" class="px-1 pb-2">
          <label class="acl-add__label">Assign selected permissions to campaigns</label>
          <multiselect-search
            v-model="assignCmps"
            :open-prefetch="true"
            :search-url="getSearchUrl"
            :search-options="searchOptions"
            skip-external-serching
            multiple
            taggable
          />
        </b-col>

        <b-col cols="12" md="4" class="px-1 pb-2">
          <b-button
            variant="primary"
            block
            :disabled="!selectedActions.length"
            @click="assignCampaigns"
          >
            Assign to selected ({{ selectedActions.length }})
          </b-button>
        </b-col>
      </b-row>

      <div class="text-muted small px-1">Leave campaigns empty to assign to all campaigns.</div>
    </div>
  </b-col>
</template>

<script>
import { aclItems } from "@/constants/acls.js";
import urls from "@/constants/urls";
import multiselectSearch from "./multiselect-search.vue";

const extendParams = ["campaign_stats", "media", "meta", "donation_levels", "donation_streams"];

export default {
  components: { multiselectSearch },

  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // permissions picked in the "Add permissions" checklist
      form: {
        actions: [],
      },
      // actions whose rows are ticked in the list
      selectedActions: [],
      // campaigns chosen for the next "Assign to selected"
      assignCmps: [],
    };
  },

  computed: {
    aclItems() {
      // only permissions not already added (one row per action)
      return aclItems.filter((el) => !this.usedACL.includes(el));
    },

    usedACL() {
      const val = this.value || [];

      return val.map((el) => `${el.action}`);
    },

    fields() {
      return [
        {
          key: "select",
          label: "",
        },
        {
          key: "action",
          label: "Action",
        },
        {
          key: "campaign_ids",
          label: "Campaigns",
        },
        {
          key: "actions",
          label: "",
        },
      ];
    },

    items() {
      const val = this.value || [];

      return val;
    },

    allSelected() {
      const val = this.value || [];

      return val.length > 0 && this.selectedActions.length === val.length;
    },

    someSelected() {
      return this.selectedActions.length > 0 && !this.allSelected;
    },

    getSearchUrl() {
      const orgID = localStorage.getItem("orgId");

      return urls.getCampaignList.replace(":orgId", orgID);
    },

    searchOptions() {
      return {
        placeholder: "Select campaigns (All)",
        changeParams: ({ page, per_page: pp }) => ({
          page: page,
          perPage: pp,
          extend: extendParams,
        }),
        customLabel: (e) => `[${e.id}] ${e.attributes.title}`,
      };
    },
  },

  methods: {
    add() {
      const val = JSON.parse(JSON.stringify(this.value || []));

      this.form.actions.forEach((action) => {
        if (!val.some((x) => x.action === action)) {
          // added with no campaigns yet (= all); scope them in the list afterwards
          val.push({ action, campaign_ids: [] });
        }
      });

      this.form.actions = [];

      this.$emit("input", val);
    },

    toggleRow(action, checked) {
      if (checked) {
        if (!this.selectedActions.includes(action)) {
          this.selectedActions = [...this.selectedActions, action];
        }
      } else {
        this.selectedActions = this.selectedActions.filter((a) => a !== action);
      }
    },

    toggleSelectAll(checked) {
      this.selectedActions = checked ? (this.value || []).map((x) => x.action) : [];
    },

    assignCampaigns() {
      const ids = this.assignCmps.map((c) => Number(c.id));

      // rebuild the list so every selected row gets the chosen campaigns appended to
      // (not replacing) what it already has; a brand-new array of brand-new row objects
      // guarantees the parent's v-model re-renders.
      const val = (this.value || []).map((row) => {
        if (this.selectedActions.includes(row.action)) {
          const merged = new Set([...(row.campaign_ids || []), ...ids]);
          return { ...row, campaign_ids: [...merged] };
        }
        return { ...row, campaign_ids: [...(row.campaign_ids || [])] };
      });

      this.$emit("input", val);

      // reset for the next selection/assignment
      this.selectedActions = [];
      this.assignCmps = [];
    },

    removeCampaign(action, cid) {
      const val = (this.value || []).map((row) => {
        if (row.action === action) {
          return {
            ...row,
            campaign_ids: (row.campaign_ids || []).filter((id) => id !== cid),
          };
        }
        return { ...row, campaign_ids: [...(row.campaign_ids || [])] };
      });

      this.$emit("input", val);
    },

    remove(i) {
      const val = JSON.parse(JSON.stringify(this.value || []));

      const [removed] = val.splice(i, 1);
      if (removed) {
        this.selectedActions = this.selectedActions.filter((a) => a !== removed.action);
      }

      this.$emit("input", val);
    },
  },
};
</script>

<style lang="scss" scoped>
.acl-add {
  &__label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 4px;
  }

  &__perm-list {
    max-height: 180px;
    overflow-y: auto;
    padding: 8px 10px;
    border: 1px solid rgba(32, 67, 78, 0.15);
    border-radius: 4px;
  }
}

.acl-cmp-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 500;

  &__x {
    margin-left: 2px;
    cursor: pointer;

    &:hover {
      color: var(--danger, #dc3545);
    }
  }
}
</style>
